import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import { fetchAnalytics, selectAnalyticsEntry } from '../../store/entities/analytics';
import { selectOptionsEstablishment, selectOptionsEstablishmentState } from '../../store/entities/optionsEstablishment';
import { FetchState } from '../../store/fetchState';
import { AnalyticsGroupDto } from '@api/models/analytics/AnalyticsGroupDto';
import { useUserPageSize } from '../../hooks/useUserPageSize';
import { AnalyticsView, ChartKind } from './components';
import { AnalyticsDomainConfig, NO_GROUP_BY } from './analyticsDomainConfig';
import { formatGroupLabel, isDrillableTimeGroupBy, isTimeGroupBy } from './analyticsDateUtils';
import { ColumnPrefsScope, loadColumnPrefs, saveColumnPrefs } from './analyticsColumnPrefs';
import { DashboardChartType } from '@api/models/enums/DashboardChartType';

const EXPLORER_KEY = 'explorer:CURRENT';
const EXPLORER_DRILL_KEY = 'explorer:DRILL_DOWN';

export interface DrillDownContext {
  label: string;
  previousGroupBy: string;
  previousFormValues: Record<string, unknown>;
  previousView: AnalyticsView;
}

export interface ExplorationConfig {
  groupBy: string;
  formValues?: Record<string, unknown>;
  tileId?: string;
  tileTitle?: string;
  /** Existing tile chart type — used to seed the edit dialog. */
  tileChartType?: DashboardChartType;
  /** Existing tile metric — used to seed the edit dialog. */
  tileMetric?: string;
}

export interface ExplorationStateSnapshot {
  groupBy: string;
  formValues: Record<string, unknown>;
  view: AnalyticsView;
  chartKind: ChartKind;
  yKey: string;
  drillDownContext?: DrillDownContext;
  /** Ordered list of column keys the user has chosen to show. Empty = use defaults from response metadata. */
  visibleColumns: string[];
}

export type ExplorationInitialState = Partial<ExplorationStateSnapshot>;

const buildPrefsScope = (domainKey: string, groupBy: string, isDrillDown: boolean): ColumnPrefsScope => ({
  domain: domainKey,
  groupBy: isDrillDown ? 'drill' : groupBy === NO_GROUP_BY ? 'rows' : groupBy,
  isDrillDown,
});

export const useAnalyticsExploration = (
  config: AnalyticsDomainConfig,
  onStateChange?: (state: ExplorationStateSnapshot) => void,
  initialState?: ExplorationInitialState,
) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const [formValues, setFormValues] = useState<Record<string, unknown>>(
    () => initialState?.formValues ?? config.defaultFormValues(),
  );
  const [groupBy, setGroupBy] = useState<string>(initialState?.groupBy ?? config.defaultGroupBy);
  const [view, setView] = useState<AnalyticsView>(initialState?.view ?? 'chart');
  const [chartKind, setChartKind] = useState<ChartKind>(initialState?.chartKind ?? 'bar');
  const [yKey, setYKey] = useState(initialState?.yKey ?? 'count');
  const [drillDownContext, setDrillDownContext] = useState<DrillDownContext | undefined>(initialState?.drillDownContext);
  const [page, setPage] = useState(0);
  // Precedence on init: URL-seeded array > localStorage > [] (let render-time apply metadata defaults).
  const [visibleColumns, setVisibleColumnsState] = useState<string[]>(() => {
    if (initialState?.visibleColumns && initialState.visibleColumns.length > 0) return initialState.visibleColumns;
    const initIsDrill = !!initialState?.drillDownContext;
    const initGroupBy = initialState?.groupBy ?? config.defaultGroupBy;
    return loadColumnPrefs(buildPrefsScope(config.key, initGroupBy, initIsDrill)) ?? [];
  });

  const { pageSize, savingPageSize, setPageSize } = useUserPageSize();
  const currentEntry = useAppSelector(selectAnalyticsEntry(EXPLORER_KEY));
  const drillDownEntry = useAppSelector(selectAnalyticsEntry(EXPLORER_DRILL_KEY));
  const optionsEstablishment = useAppSelector(selectOptionsEstablishment);
  const optionsEstablishmentState = useAppSelector(selectOptionsEstablishmentState);

  const establishmentCount = optionsEstablishment.length;
  const hasSingleEstablishment = optionsEstablishmentState === FetchState.Loaded && optionsEstablishment.length <= 1;

  const groupByOptions = useMemo(
    () =>
      hasSingleEstablishment && config.singleEstablishmentHiddenGroupBys
        ? config.groupByOptions.filter((o) => !config.singleEstablishmentHiddenGroupBys!.includes(o.value))
        : config.groupByOptions,
    [hasSingleEstablishment, config.groupByOptions, config.singleEstablishmentHiddenGroupBys],
  );

  const isDrillDown = !!drillDownContext;
  const isRowsMode = isDrillDown || groupBy === NO_GROUP_BY;
  const activeEntry = isRowsMode ? drillDownEntry : currentEntry;

  const activeFilterTags = useMemo(() => config.buildFilterTags(formValues, t, i18n, { establishmentCount }), [config, formValues, t, i18n, establishmentCount]);

  const emitSnapshot = useCallback(
    (overrides: Partial<ExplorationStateSnapshot>) => {
      onStateChange?.({ groupBy, formValues, view, chartKind, yKey, drillDownContext, visibleColumns, ...overrides });
    },
    [onStateChange, groupBy, formValues, view, chartKind, yKey, drillDownContext, visibleColumns],
  );

  const fetchCurrent = useCallback(
    (fv: Record<string, unknown>, gb: string, size: number) => {
      if (gb === NO_GROUP_BY) {
        setPage(0);
        const params = config.toDrillDownQueryParams(fv, { page: 0, size });
        dispatch(fetchAnalytics(EXPLORER_DRILL_KEY, () => config.fetchAnalytics(params)));
      } else {
        const params = config.toQueryParams(fv, gb);
        dispatch(fetchAnalytics(EXPLORER_KEY, () => config.fetchAnalytics(params)));
      }
    },
    [dispatch, config],
  );

  const initialize = useCallback(
    (
      initialConfig?: ExplorationConfig,
      options?: {
        view?: AnalyticsView;
        chartKind?: ChartKind;
        yKey?: string;
        drillDownContext?: DrillDownContext;
        visibleColumns?: string[];
        skipStateChange?: boolean;
      },
    ) => {
      const fv = initialConfig?.formValues ?? config.defaultFormValues();
      const gb = initialConfig?.groupBy ?? config.defaultGroupBy;
      const initView = options?.view ?? 'chart';
      const initChartKind = options?.chartKind ?? 'bar';
      const initYKey = options?.yKey ?? 'count';
      const initDrill = options?.drillDownContext;
      const seededColumns = options?.visibleColumns ?? [];
      const initColumns = seededColumns.length > 0
        ? seededColumns
        : loadColumnPrefs(buildPrefsScope(config.key, gb, !!initDrill)) ?? [];
      setFormValues(fv);
      setGroupBy(gb);
      setDrillDownContext(initDrill);
      setView(initView);
      setChartKind(initChartKind);
      setYKey(initYKey);
      setVisibleColumnsState(initColumns);
      setPage(0);
      if (initDrill) {
        const params = config.toDrillDownQueryParams(fv, { page: 0, size: pageSize });
        dispatch(fetchAnalytics(EXPLORER_DRILL_KEY, () => config.fetchAnalytics(params)));
      } else {
        fetchCurrent(fv, gb, pageSize);
      }
      if (initialConfig && !options?.skipStateChange) {
        emitSnapshot({ groupBy: gb, formValues: fv, view: initView, chartKind: initChartKind, yKey: initYKey, drillDownContext: initDrill, visibleColumns: initColumns });
      }
    },
    [fetchCurrent, config, dispatch, emitSnapshot, pageSize],
  );

  const applyFilters = useCallback(
    (next: Record<string, unknown>) => {
      setFormValues(next);
      setDrillDownContext(undefined);
      fetchCurrent(next, groupBy, pageSize);
      emitSnapshot({ formValues: next, drillDownContext: undefined });
    },
    [fetchCurrent, groupBy, pageSize, emitSnapshot],
  );

  const changeGroupBy = useCallback(
    (nextGroupBy: string) => {
      setGroupBy(nextGroupBy);
      setDrillDownContext(undefined);
      const nextView: AnalyticsView = nextGroupBy === NO_GROUP_BY ? 'table' : view;
      if (nextView !== view) setView(nextView);
      const nextColumns = loadColumnPrefs(buildPrefsScope(config.key, nextGroupBy, false)) ?? [];
      setVisibleColumnsState(nextColumns);
      fetchCurrent(formValues, nextGroupBy, pageSize);
      emitSnapshot({ groupBy: nextGroupBy, view: nextView, drillDownContext: undefined, visibleColumns: nextColumns });
    },
    [fetchCurrent, formValues, view, pageSize, emitSnapshot, config.key],
  );

  const canDrillDown = groupBy !== NO_GROUP_BY && (!isTimeGroupBy(groupBy) || isDrillableTimeGroupBy(groupBy));

  const drillDownImpl = useCallback(
    (group: AnalyticsGroupDto) => {
      const next = config.applyDrillDown(formValues, groupBy, group);
      if (!next) return;

      const label = formatGroupLabel(group.groupKey, groupBy, '', i18n);
      const ctx: DrillDownContext = {
        label,
        previousGroupBy: groupBy,
        previousFormValues: formValues,
        previousView: view,
      };

      setDrillDownContext(ctx);
      setPage(0);
      setFormValues(next);
      setView('table');
      // Drill-down always resets columns to defaults (per design: different mode → different prefs slot, not persisted).
      setVisibleColumnsState([]);

      const params = config.toDrillDownQueryParams(next, { page: 0, size: pageSize });
      dispatch(fetchAnalytics(EXPLORER_DRILL_KEY, () => config.fetchAnalytics(params)));
      emitSnapshot({ formValues: next, view: 'table', drillDownContext: ctx, visibleColumns: [] });
    },
    [dispatch, formValues, groupBy, view, pageSize, config, i18n, emitSnapshot],
  );

  const drillDown = canDrillDown ? drillDownImpl : undefined;

  const clearDrillDown = useCallback(() => {
    if (!drillDownContext) return;
    const restored = drillDownContext.previousFormValues;
    const gb = drillDownContext.previousGroupBy;
    const restoredView = drillDownContext.previousView;
    setFormValues(restored);
    setDrillDownContext(undefined);
    setView(restoredView);
    setGroupBy(gb);
    const restoredColumns = loadColumnPrefs(buildPrefsScope(config.key, gb, false)) ?? [];
    setVisibleColumnsState(restoredColumns);

    const params = config.toQueryParams(restored, gb);
    dispatch(fetchAnalytics(EXPLORER_KEY, () => config.fetchAnalytics(params)));
    emitSnapshot({ groupBy: gb, formValues: restored, view: restoredView, drillDownContext: undefined, visibleColumns: restoredColumns });
  }, [dispatch, drillDownContext, config, emitSnapshot]);

  const changePage = useCallback(
    (nextPage: number) => {
      setPage(nextPage);
      const params = config.toDrillDownQueryParams(formValues, { page: nextPage, size: pageSize });
      dispatch(fetchAnalytics(EXPLORER_DRILL_KEY, () => config.fetchAnalytics(params)));
    },
    [dispatch, formValues, pageSize, config],
  );

  const changePageSize = useCallback(
    async (nextSize: number) => {
      await setPageSize(nextSize);
      setPage(0);
      if (isRowsMode) {
        const params = config.toDrillDownQueryParams(formValues, { page: 0, size: nextSize });
        dispatch(fetchAnalytics(EXPLORER_DRILL_KEY, () => config.fetchAnalytics(params)));
      }
    },
    [setPageSize, dispatch, formValues, config, isRowsMode],
  );

  const handleSetView = useCallback(
    (nextView: AnalyticsView) => {
      if (nextView === 'chart' && groupBy === NO_GROUP_BY && !isDrillDown) {
        const nextGroupBy = config.defaultGroupBy;
        setView(nextView);
        setGroupBy(nextGroupBy);
        fetchCurrent(formValues, nextGroupBy, pageSize);
        emitSnapshot({ view: nextView, groupBy: nextGroupBy });
        return;
      }
      setView(nextView);
      emitSnapshot({ view: nextView });
    },
    [emitSnapshot, groupBy, isDrillDown, config, formValues, fetchCurrent, pageSize],
  );

  const handleSetChartKind = useCallback(
    (nextChartKind: ChartKind) => {
      setChartKind(nextChartKind);
      emitSnapshot({ chartKind: nextChartKind });
    },
    [emitSnapshot],
  );

  const handleSetYKey = useCallback(
    (nextYKey: string) => {
      setYKey(nextYKey);
      emitSnapshot({ yKey: nextYKey });
    },
    [emitSnapshot],
  );

  const setVisibleColumns = useCallback(
    (next: string[]) => {
      setVisibleColumnsState(next);
      saveColumnPrefs(buildPrefsScope(config.key, groupBy, isDrillDown), next);
      emitSnapshot({ visibleColumns: next });
    },
    [config.key, groupBy, isDrillDown, emitSnapshot],
  );

  return {
    formValues,
    groupBy,
    view,
    chartKind,
    yKey,
    drillDownContext,
    isDrillDown,
    isRowsMode,
    page,
    pageSize,
    savingPageSize,
    activeEntry,
    currentEntry,
    drillDownEntry,
    activeFilterTags,
    establishmentCount,
    groupByOptions,
    visibleColumns,

    setView: handleSetView,
    setChartKind: handleSetChartKind,
    setYKey: handleSetYKey,
    setVisibleColumns,

    initialize,
    applyFilters,
    changeGroupBy,
    drillDown,
    clearDrillDown,
    changePage,
    changePageSize,
  };
};
